import { FilterItem } from '@mm/components';
import { SubscriptionFeatureFlag } from '@mm/state/controllers/FeatureFlags';
import { selectOverviewCountByUiFilterKind } from '@mm/state/selectors/campaignsOverviewSelectors';
import {
  combinedFeatureFlagsSelector,
  nextBillingTypeForFeatureSelector,
} from '@mm/state/selectors/featureFlagsSelectors';
import { StoreState } from '@mm/state/store-interfaces';
import { ReactComponent as FBIcon } from 'assets/messenger-fb-new.svg';
import { ReactComponent as SMSIcon } from 'assets/smartphone.svg';
import classNames from 'classnames';
import { keys } from 'lodash';
import React from 'react';
import { useSelector } from 'react-redux';
import { PlanBadge } from '../../../../../../common/components/ProBadge';
import { trackWebAppGAEvent } from '../../../../../../common/helpers/plugins/website-google-analytics';
import { useGreenCueMarkers } from '../../../../../../common/hooks/use-green-cue-markers';
import { useFreeTrialUpgradeModal } from '../../../../../../common/modals/FreeTrialUpgradeModal/use-free-trial-upgrade-modal';
import { campaignTypeByKind, UIFilterCampaignKindType } from '../types';
import './CampaignsFilterByKind.pcss';
import { getUniqId } from '@mm/utils/src/get-uniq-id';

type Props = {
  className?: string;
  activeKind?: UIFilterCampaignKindType;
  onChange?: (kind?: UIFilterCampaignKindType) => void;
  messagingApproved?: boolean;
};

const requiredFeatures: Record<
  UIFilterCampaignKindType,
  SubscriptionFeatureFlag | undefined
> = {
  all: undefined,
  chat_blaster: 'chatBlasters',
  drip_campaigns: 'dripCampaigns',
  ctm_ads: 'adsUnlimitedNewCtmAds',
  legacy_messenger_ads: undefined,
  sponsored_messages: 'adsSponsored',
  rss_blasts: undefined,
  automation: undefined,
};

type CampaignFilterType = 'fbSms' | 'facebook';

type CampaignFilterByTypeProps = {
  kindsToShow: UIFilterCampaignKindType[];
  type: CampaignFilterType;
  onChange?: (kind?: UIFilterCampaignKindType) => void;
  activeKind?: UIFilterCampaignKindType;
  messagingApproved?: boolean;
};

const kindsByType: Record<CampaignFilterType, UIFilterCampaignKindType[]> = {
  fbSms: ['chat_blaster', 'drip_campaigns'],
  facebook: [
    'ctm_ads',
    'legacy_messenger_ads',
    'sponsored_messages',
    'rss_blasts',
  ],
};

const typeTitles: Record<CampaignFilterType, string> = {
  fbSms: 'Facebook & SMS',
  facebook: 'Facebook',
};

const getIconByType = (type: CampaignFilterType) => {
  switch (type) {
    case 'fbSms':
      return (
        <div className="icons-container">
          <FBIcon className="icon-by-type" />
          <SMSIcon className="icon-by-type" />
        </div>
      );
    case 'facebook':
      return <FBIcon className="icon-by-type" />;
  }
};

const CampaignFilterBlockByType = ({
  type,
  kindsToShow,
  onChange,
  activeKind,
  messagingApproved,
}: CampaignFilterByTypeProps) => {
  const icon = getIconByType(type);

  return (
    <div>
      <div className="campaign-filter-by-type__heading">
        {icon}
        {typeTitles[type]}
        <div className="campaign-filter-by-type__heading-line" />
      </div>
      {kindsToShow.map(kind => (
        <CampaignsFilterByKindItem
          key={getUniqId()}
          kind={kind}
          onChange={onChange}
          activeKind={activeKind}
          messagingApproved={messagingApproved}
        />
      ))}
    </div>
  );
};

const CampaignsFilterByKindItem = ({
  kind,
  onChange = () => {},
  activeKind,
  messagingApproved,
}: Props & {
  kind: UIFilterCampaignKindType;
}) => {
  const count = useSelector((state: StoreState) =>
    selectOverviewCountByUiFilterKind(state, kind),
  );

  const showActivationCues = useSelector(
    combinedFeatureFlagsSelector,
  ).show_cues;
  const { name, icon: Icon } = campaignTypeByKind[kind] || {};

  const { isChatBlasterActivated, isDripCampaignsActivated } =
    useGreenCueMarkers();
  const { isFreeTrialPromotionWithoutSubscription } =
    useFreeTrialUpgradeModal();

  const requiredFeature = requiredFeatures[kind];

  const billingType = useSelector((state: StoreState) =>
    nextBillingTypeForFeatureSelector(state, requiredFeature),
  );

  if (
    (kind === 'legacy_messenger_ads' && !count) ||
    (!messagingApproved && kind === 'rss_blasts')
  ) {
    return null;
  }

  // Note: We introduce inter-dependency between the cues here.
  // Drips are more important than regular blasts, so we want to show Drips first
  const showChatBlasterGreenMarker =
    !isChatBlasterActivated &&
    kind === 'chat_blaster' &&
    isDripCampaignsActivated;
  const showDripCampaignGuardGreenMarker =
    kind === 'drip_campaigns' && !isDripCampaignsActivated;
  const showGreenMarker =
    showActivationCues &&
    isFreeTrialPromotionWithoutSubscription &&
    (showChatBlasterGreenMarker || showDripCampaignGuardGreenMarker);

  return (
    <FilterItem<UIFilterCampaignKindType | undefined>
      key={name}
      item={kind}
      title={name}
      icon={Icon}
      onClick={() => {
        trackWebAppGAEvent('click', {
          category: 'campaign_filter_by_type',
          label: `campaign_filter_${kind}`,
        });
        onChange(kind);
      }}
      isActive={kind === activeKind}
      showGreenMarker={showGreenMarker}
    >
      {!!count && (
        <span>
          <span>&nbsp;</span>(
          <span className="campaign-filter-item__count-number">{count}</span>)
        </span>
      )}
      {billingType && (
        <PlanBadge
          className="campaign-filter-item__plan-badge"
          billingType={billingType}
          size={10}
        />
      )}
    </FilterItem>
  );
};

export const CampaignsFilterByKind = (props: Props) => {
  const { className, onChange, activeKind, messagingApproved } = props;
  const types = keys(kindsByType) as CampaignFilterType[];

  return (
    <div className={classNames(className)}>
      {types.map(type => (
        <CampaignFilterBlockByType
          key={getUniqId() + type}
          type={type}
          kindsToShow={kindsByType[type]}
          onChange={onChange}
          activeKind={activeKind}
          messagingApproved={messagingApproved}
        />
      ))}
    </div>
  );
};
